import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type Theme = 'light' | 'dark';
type Language = 'mn' | 'en';
type TranslationMap = Record<string, string>;

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
  language: Language;
  toggleLanguage: () => void;
  t: (key: string, obj?: TranslationMap) => string;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getInitialTheme = (): Theme => {
  return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
};

const getInitialLanguage = (): Language => {
  return localStorage.getItem('language') === 'en' ? 'en' : 'mn';
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Theme State
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // Language State
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  // Theme Change Effect
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Language Change Effect
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === 'mn' ? 'en' : 'mn'));
  }, []);

  // Translation Helper
  const t = useCallback(
    (key: string, obj?: TranslationMap) => {
      if (!obj) return key;
      return obj[language] || obj['mn'] || key;
    },
    [language]
  );
  const contextValue = useMemo(
    () => ({ theme, toggleTheme, language, toggleLanguage, t }),
    [theme, toggleTheme, language, toggleLanguage, t]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
